import { useEffect, useRef, useState } from 'react';

export default function WhyBeirutSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 bg-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, #102a43 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <div className={`space-y-8 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="space-y-4">
              <span className="text-champagne-600 text-sm font-sans font-medium tracking-[0.2em] uppercase">
                The Intersection
              </span>
              
              <h2 className="font-display text-5xl lg:text-6xl font-bold text-navy-950 leading-[1.1]">
                Why Beirut,<br />Why Now
              </h2>
              
              <div className="w-16 h-0.5 bg-champagne-500"></div>
            </div>

            <div className="space-y-6">
              <p className="text-lg text-navy-700 font-sans leading-relaxed">
                Beirut stands at the intersection of Europe, the Gulf, and the Levant — a city of resilience, culture, and reinvention.
              </p>
              
              <p className="text-lg text-navy-700 font-sans leading-relaxed">
                In March 2026, curated by the <strong className="text-navy-900 font-semibold">WealthRise Management team</strong>, it becomes the meeting point for women who allocate capital, steward family legacies, and influence the world's most powerful ecosystems.
              </p>
            </div>

            {/* Key Points */}
            <div className="grid grid-cols-3 gap-6 pt-8">
              <div className="space-y-2">
                <div className="w-12 h-12 flex items-center justify-center bg-navy-50 rounded-full">
                  <i className="ri-global-line text-2xl text-navy-700"></i>
                </div>
                <p className="text-sm font-sans font-medium text-navy-900">Global<br />Crossroads</p>
              </div>
              
              <div className="space-y-2">
                <div className="w-12 h-12 flex items-center justify-center bg-navy-50 rounded-full">
                  <i className="ri-building-line text-2xl text-navy-700"></i>
                </div>
                <p className="text-sm font-sans font-medium text-navy-900">Cultural<br />Heritage</p>
              </div>
              
              <div className="space-y-2">
                <div className="w-12 h-12 flex items-center justify-center bg-navy-50 rounded-full">
                  <i className="ri-lightbulb-line text-2xl text-navy-700"></i>
                </div>
                <p className="text-sm font-sans font-medium text-navy-900">Strategic<br />Reinvention</p>
              </div>
            </div>
          </div>

          {/* Right Column - Image */}
          <div className={`relative transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative h-[600px] rounded-sm overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/d6ebd9d87088f64a172b1ece4ac7bace.jpg"
                alt="Beirut Cityscape"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/40 via-transparent to-transparent"></div>
              
              {/* Floating Card */}
              <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-sm p-6 shadow-2xl">
                <p className="font-display text-2xl text-navy-950 italic leading-relaxed">
                  "Where resilience meets refinement"
                </p>
              </div>
            </div>

            {/* Decorative Element */}
            <div className="absolute -top-6 -right-6 w-32 h-32 border-2 border-champagne-400 rounded-full -z-10"></div>
          </div>
        </div>
      </div>
    </section>
  );
}